import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useMatch } from "@tanstack/react-router";
import type { StatefulDiaryT } from "@patient-watch/types";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import { diaryByIdQueryOptions } from "../api/diaryQueryOptions";
import { useDiariesByPatientQuery } from "../api/diaryQueries";
import type { DiaryForTodoCounters } from "./diaryForTodoCounters";

function mergeDiaryListWithRouteDetail(
    diaries: DiaryForTodoCounters[],
    routeDiary: StatefulDiaryT | null | undefined
): DiaryForTodoCounters[] {
    if (!routeDiary) {
        return diaries;
    }

    const index = diaries.findIndex((d) => d.diary_id === routeDiary.diary_id);
    if (index === -1) {
        return [...diaries, routeDiary];
    }

    const next = [...diaries];
    next[index] = routeDiary;
    return next;
}

/**
 * Patient diary list merged with the active diary detail query when the user is
 * on a diary route. Keeps nav / badge counters aligned with the to-do page, which
 * always reads fresh state from diary-by-id.
 */
export function useDiariesForTodoCounters(patientId: string): DiaryForTodoCounters[] {
    const { data: diaries } = useDiariesByPatientQuery(patientId);
    const { locale } = useAppLocale();
    const match = useMatch({
        from: "/_app/diaries/$diaryId",
        shouldThrow: false,
    });
    const diaryId = match?.params.diaryId;
    const { data: routeDiary } = useQuery({
        ...diaryByIdQueryOptions(diaryId ?? "", locale),
        enabled: Boolean(patientId) && Boolean(diaryId),
    });

    return useMemo(() => mergeDiaryListWithRouteDetail(diaries, routeDiary), [diaries, routeDiary]);
}
